import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Users, FileSignature, ShieldCheck, TrendingUp, ArrowRight, CheckCircle2, AlertTriangle, Loader2, FileDown, Send, Moon, Utensils } from "lucide-react";
import { toast } from "sonner";

export default function Overview() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [parentInvoices, setParentInvoices] = useState([]);
  const [center, setCenter] = useState(null);
  const [digestBusy, setDigestBusy] = useState(null);  // "preview" | "send" | null

  useEffect(() => {
    Promise.all([
      api.get("/compliance/summary").then((r) => r.data),
      api.get("/billing/invoices").then((r) => r.data),
      api.get("/center/me").then((r) => r.data),
      api.get("/parent-invoices").then((r) => r.data).catch(() => []),
    ]).then(([s, i, c, pi]) => {
      setSummary(s); setInvoices(i); setCenter(c); setParentInvoices(pi);
    });
  }, []);

  const openNightlyDigest = async () => {
    setDigestBusy("preview");
    try {
      const res = await api.get("/compliance/nightly-report/preview.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDigestBusy(null); }
  };

  const sendNightlyDigest = async () => {
    setDigestBusy("send");
    try {
      const res = await api.post("/compliance/nightly-report/send");
      const d = res.data || {};
      if (d.ok) {
        toast.success(`Nightly digest emailed · ${d.total_issues ?? 0} issue${(d.total_issues ?? 0) === 1 ? "" : "s"} flagged`);
      } else {
        toast.message("Digest generated — email provider not configured yet.", { description: d.reason || "Set SENDER_EMAIL + GMAIL_APP_PASSWORD (or SendGrid) to enable morning delivery." });
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDigestBusy(null); }
  };

  // iter-78: "Revenue this month" now filters to the current calendar month
  // and includes BOTH ODJFS-imported billing (state subsidies) AND private-pay
  // parent invoices (iter-77 flow). Outstanding stays lifetime because it's
  // "still open" — not scoped to a month.
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const inCurrentMonth = (isoDate) => {
    if (!isoDate) return false;
    return String(isoDate).slice(0, 7) === monthKey;
  };
  const odjfsRevenueMTD = invoices.reduce((sum, i) => {
    if (i.status !== "paid") return sum;
    const paidDate = i.paid_at || i.payment_date || i.issued_at;
    return inCurrentMonth(paidDate) ? sum + (i.amount_usd || 0) : sum;
  }, 0);
  const parentRevenueMTD = parentInvoices.reduce((sum, i) => {
    if (i.status !== "paid") return sum;
    return inCurrentMonth(i.paid_at) ? sum + (i.amount || 0) : sum;
  }, 0);
  const totalRevenue = odjfsRevenueMTD + parentRevenueMTD;
  const outstanding = invoices.reduce((sum, i) => sum + (i.status !== "paid" ? (i.amount_usd || 0) : 0), 0)
                    + parentInvoices.reduce((sum, i) => sum + (i.status === "issued" ? (i.amount || 0) : 0), 0);

  // iter-81x — quick shortcut: print this week's food menu (auto-computes
  // this Monday + rotation week 1-4, no picker required).
  const [thisWeekBusy, setThisWeekBusy] = useState(false);
  const printThisWeekMenu = async () => {
    setThisWeekBusy(true);
    try {
      const res = await api.post("/menus/pdf-this-week", null, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        const a = document.createElement("a");
        a.href = url; a.download = `food-menu-this-week.pdf`; a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setThisWeekBusy(false); }
  };

  const stats = [
    { label: "Children enrolled", value: summary?.total_children ?? "—", Icon: Users, tone: "emerald" },
    { label: "DCY 01234 signed", value: `${summary?.jfs_01234_signed ?? 0} / ${summary?.total_children ?? 0}`, Icon: FileSignature, tone: "orange" },
    { label: "Audit readiness", value: `${summary?.percent_complete ?? 0}%`, Icon: ShieldCheck, tone: "emerald" },
    { label: "Revenue this month", value: `$${totalRevenue.toLocaleString()}`, Icon: TrendingUp, tone: "stone" },
  ];

  return (
    <div data-testid="overview-page" className="space-y-10">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">
          {center?.name || "Your center"}
        </div>
        <h1 className="mt-2 font-display text-4xl font-medium tracking-tight text-stone-900">
          Good day, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="mt-2 text-stone-500">Here's a calm look at your center right now.</p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
            className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-stone-500">{s.label}</span>
              <s.Icon className={`w-4 h-4 ${s.tone === "emerald" ? "text-emerald-700" : s.tone === "orange" ? "text-orange-600" : "text-stone-700"}`} />
            </div>
            <div className="mt-3 font-display text-3xl font-medium text-stone-900">{s.value}</div>
          </div>
        ))}
      </div>

      {/* iter-81x — Quick action: print this week's food menu (auto-computes
          Monday + rotation week, both age groups). One click. */}
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 flex flex-wrap items-center gap-4" data-testid="overview-this-week-menu">
        <div className="w-11 h-11 rounded-xl bg-emerald-700 text-white flex items-center justify-center shrink-0">
          <Utensils className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs uppercase tracking-widest font-semibold text-emerald-800">Print this week's menu</div>
          <div className="text-sm text-stone-700 mt-0.5">
            One click - auto-picks this Monday and both age groups. Skip the picker.
          </div>
        </div>
        <Button
          data-testid="overview-print-this-week-menu"
          onClick={printThisWeekMenu}
          disabled={thisWeekBusy}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10"
        >
          {thisWeekBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
          Print this week
        </Button>
        <Link
          to="/dashboard/licensing/food-menu"
          className="text-sm text-emerald-700 font-semibold flex items-center gap-1 hover:underline"
        >
          Open picker <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <section className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 rounded-2xl border border-stone-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-medium text-stone-900">Live OAC 5101:2-12-18 ratios</h2>
            <Link to="/dashboard/compliance" className="text-sm text-emerald-700 font-semibold flex items-center gap-1">
              Details <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="mt-4 space-y-2.5" data-testid="overview-ratios">
            {(summary?.ratios || []).map((r) => (
              <div key={r.age_group} className="flex items-center justify-between py-2.5 border-b border-stone-100 last:border-0">
                <div>
                  <div className="text-sm font-medium text-stone-900">{r.label}</div>
                  <div className="text-[11px] text-stone-500">Required {r.required_ratio}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-base font-semibold text-stone-900">{r.children} children</div>
                  <div className="text-[11px] text-emerald-700">{r.staff_needed} staff needed</div>
                </div>
              </div>
            ))}
            {(!summary?.ratios || summary.ratios.length === 0) && (
              <p className="text-sm text-stone-500 py-6 text-center">No children yet. <Link to="/dashboard/children" className="text-emerald-700 font-semibold">Add your first.</Link></p>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-stone-200 bg-white p-6">
            <h2 className="font-display text-xl font-medium text-stone-900">Compliance</h2>
            <div className="mt-4 flex items-center gap-3">
              {summary && summary.jfs_01234_missing > 0 ? (
                <>
                  <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0" />
                  <div className="text-sm text-stone-700">
                    <span className="font-semibold">{summary.jfs_01234_missing}</span> children are missing DCY 01234. <Link to="/dashboard/children" className="text-emerald-700 font-semibold">Fix now →</Link>
                  </div>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />
                  <div className="text-sm text-stone-700">All children have a signed DCY 01234.</div>
                </>
              )}
            </div>
            <div className="mt-4 h-1.5 rounded-full bg-stone-100 overflow-hidden">
              <div className="h-full bg-emerald-700 rounded-full" style={{ width: `${summary?.percent_complete || 0}%` }} />
            </div>
          </div>

          <div className="rounded-2xl border border-emerald-900 bg-emerald-950 text-stone-50 p-6" data-testid="nightly-digest-card">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 shrink-0 rounded-lg grid place-items-center bg-emerald-800/80">
                <Moon className="w-4 h-4 text-emerald-200" />
              </span>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-lg font-medium">Nightly compliance digest</h2>
                <p className="mt-1 text-[13px] text-emerald-100/80 leading-relaxed">
                  One PDF emailed to you every morning — expired staff docs, missing enrollment packets,
                  overdue playground inspections and the week's attendance gaps, all in one scan.
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                data-testid="nightly-digest-preview-btn"
                onClick={openNightlyDigest}
                disabled={!!digestBusy}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl h-9 text-sm"
              >
                {digestBusy === "preview" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                Preview tonight's digest
              </Button>
              <Button
                data-testid="nightly-digest-send-btn"
                onClick={sendNightlyDigest}
                disabled={!!digestBusy}
                variant="ghost"
                className="text-emerald-100 hover:text-white hover:bg-emerald-800/60 rounded-xl h-9 text-sm"
              >
                {digestBusy === "send" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Email it to me now
              </Button>
            </div>
            <p className="mt-3 text-[10px] uppercase tracking-wider text-emerald-200/70 font-semibold">
              Auto-delivered at 6 AM · your inbox
            </p>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-stone-900 text-stone-50 p-6">
            <h2 className="font-display text-lg font-medium">Tuition outstanding</h2>
            <div className="mt-3 font-display text-3xl font-semibold">${outstanding.toLocaleString()}</div>
            <Link to="/dashboard/billing" className="mt-3 inline-flex items-center gap-1 text-sm text-emerald-300 font-semibold">
              View invoices <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <BirthdayThisWeekCard />
    </div>
  );
}

function BirthdayThisWeekCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  const load = () => api.get("/birthdays/this-week").then((r) => setRows(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const sendGreeting = async (row) => {
    if (!row.parent_email) { toast.error("No parent email on file."); return; }
    if (!window.confirm(`Send a birthday greeting + photo permission slip to ${row.parent_email}?`)) return;
    setSendingId(row.child_id);
    try {
      const res = await api.post(`/birthdays/${row.child_id}/send-greeting`);
      if (res.data?.already_sent) toast.info(`Already sent earlier — ${row.parent_email}`);
      else if (res.data?.sent) toast.success(`Birthday email sent to ${row.parent_email}`);
      else toast.warning(`Saved — email service: ${res.data?.reason || "no provider"}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSendingId(null); }
  };

  if (loading) return null;

  return (
    <section data-testid="birthdays-this-week" className="rounded-3xl border border-stone-200 bg-white p-7">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Parent delight</div>
          <h2 className="mt-2 font-display text-2xl text-stone-900">Birthdays this week 🎂</h2>
          <p className="mt-1 text-sm text-stone-500">One tap sends a warm note + photo permission slip to the family.</p>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-stone-500">{rows.length} celebrating</div>
      </header>

      {rows.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 p-8 text-center">
          <p className="text-sm text-stone-500">Quiet week — no birthdays in your roster between Mon and Sun. 🎈</p>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-stone-100" data-testid="birthdays-list">
          {rows.map((r) => {
            const alreadySent = !!r.greeting_year && r.greeting_year === new Date().getFullYear();
            return (
              <li key={r.child_id} data-testid={`birthday-row-${r.child_id}`} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center text-xl">🎂</div>
                  <div>
                    <div className="font-medium text-stone-900">{r.first_name} {r.last_name}</div>
                    <div className="text-[12px] text-stone-500">
                      {r.weekday} · turns <b>{r.age_turning ?? "?"}</b>
                      {r.parent_email ? <span> · {r.parent_email}</span> : <span className="text-rose-600"> · no parent email</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {alreadySent && (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Greeted ✓</span>
                  )}
                  <Button
                    type="button"
                    data-testid={`birthday-send-${r.child_id}`}
                    onClick={() => sendGreeting(r)}
                    disabled={sendingId === r.child_id || !r.parent_email}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-9 text-xs"
                  >
                    {sendingId === r.child_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    {alreadySent ? "Resend" : "Send greeting"}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
